import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  List,
  ListItem,
  Paper,
  Stack,
  Typography
} from '@mui/material'
import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { useSelectPricingMatrix } from '../../../hooks/useSelectPricingMatrix/useSelectPricingMatrix'
import { LabelsPartsInfo } from '../../../models/Labels'
import {
  CannedJobMotorFluidsInfoUnitOfMeasureEnum,
  MotorTaxonomy
} from '../../../sdk'
import {
  generateMotorJobName,
  getTaxonomyNameMap
} from '../../../utils/convertTaxonomy/convertTaxonomy'
import { CenteredLoading } from '../../CenteredLoading/CenteredLoading'
import {
  ControlledMuiCheckboxSingle,
  ControlledMuiSelect,
  ControlledMuiTextField
} from '../../FormField'
import { TaxonomyTree } from '../../SharedUI/TaxonomyTree'
import { VALIDATE_CANNEDJOB_MOTOR_FLUIDS } from '../validation'
import {
  CannedJobMotorFluidsFormInput,
  CONTENT_HEIGHT
} from './LookupMotorFluidsWindow'

interface Props {
  cancelCallback: () => void
  onSubmit: (data: CannedJobMotorFluidsFormInput, selectedTree: string) => void
  motorTaxonomy: MotorTaxonomy | undefined
  defaultInput: CannedJobMotorFluidsFormInput
  defaultSelectedTree: string
}

export const SearchFluidsTaxonomyWithoutVehicle: FC<Props> = ({
  cancelCallback,
  onSubmit,
  motorTaxonomy,
  defaultInput,
  defaultSelectedTree
}) => {
  const [selectedTree, setSelectedTree] =
    React.useState<string>(defaultSelectedTree)
  const nameMap = useMemo(
    () => getTaxonomyNameMap(motorTaxonomy, selectedTree),
    [selectedTree, motorTaxonomy]
  )
  const { handleSubmit, setValue, control } = useForm<
    CannedJobMotorFluidsFormInput & { selectedTree: string }
  >({
    mode: 'onBlur',
    defaultValues: defaultInput,
    resolver: yupResolver(VALIDATE_CANNEDJOB_MOTOR_FLUIDS)
  })
  const handleSelect = useCallback(
    async (event: React.SyntheticEvent, nodeId: string) => {
      if (!motorTaxonomy) {
        return
      }
      setSelectedTree(nodeId)
      setValue(
        'name',
        generateMotorJobName(getTaxonomyNameMap(motorTaxonomy, nodeId))
      )
    },
    [setValue, motorTaxonomy]
  )

  const {
    selectedPartsMatrixId,
    SelectPriceLevelComponent,
    SelectPartsMatrixComponent
  } = useSelectPricingMatrix('', '', defaultInput.partsMatrix || '')

  useEffect(() => {
    setValue('partsMatrix', selectedPartsMatrixId)
  }, [selectedPartsMatrixId, setValue])

  return (
    <>
      <DialogContent
        sx={{
          height: CONTENT_HEIGHT,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
        <Grid container spacing={2} sx={{ height: '100%', overflow: 'hidden' }}>
          <Grid item xs={4} sx={{ height: '100%', overflow: 'hidden' }}>
            <Paper
              variant="outlined"
              sx={{
                overflowY: 'auto',
                height: '100%',
                boxSizing: 'border-box'
              }}>
              {motorTaxonomy && (
                <TaxonomyTree
                  taxonomy={motorTaxonomy}
                  onNodeSelect={handleSelect}
                  selected={selectedTree}
                />
              )}
              {!motorTaxonomy && <CenteredLoading />}
            </Paper>
          </Grid>
          <Grid item xs={8} sx={{ height: '100%', overflow: 'hidden' }}>
            <Paper
              variant="outlined"
              sx={{
                overflowY: 'auto',
                height: '100%',
                boxSizing: 'border-box'
              }}>
              <List disablePadding>
                {Object.keys(nameMap).map((key) => (
                  <ListItem selected={true} divider key={key + 'title'}>
                    <Typography variant="subtitle1">
                      Selected {key}:{' '}
                      {nameMap[key as keyof typeof nameMap] || 'All'}
                    </Typography>
                  </ListItem>
                ))}
              </List>
              <form
                id="cannedJobMotorLaborForm"
                onSubmit={handleSubmit((data) => onSubmit(data, selectedTree))}>
                <Stack spacing={2} p={2}>
                  <ControlledMuiTextField
                    variant="standard"
                    fullWidth
                    name="name"
                    control={control}
                    label={LabelsPartsInfo.name}
                    required
                  />
                  <Stack direction="row" spacing={2}>
                    <SelectPriceLevelComponent variant="standard" />
                    <SelectPartsMatrixComponent
                      variant="standard"
                      customNoneText="Custom Pricing"
                    />
                    {!selectedPartsMatrixId && (
                      <ControlledMuiTextField
                        variant="standard"
                        fullWidth
                        control={control}
                        name="suggestedPricePerUnit"
                        label={LabelsPartsInfo.suggestedPricePerUnit}
                      />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <ControlledMuiTextField
                      variant="standard"
                      fullWidth
                      name="suggestedQuantity"
                      control={control}
                      label={LabelsPartsInfo.suggestedQuantity}
                    />
                    <ControlledMuiSelect
                      variant="standard"
                      name="unitOfMeasure"
                      control={control}
                      options={Object.values(
                        CannedJobMotorFluidsInfoUnitOfMeasureEnum
                      )
                        .filter(
                          (u) =>
                            u !==
                            CannedJobMotorFluidsInfoUnitOfMeasureEnum.DiscreteUnit
                        )
                        .map((u) => ({
                          id: u,
                          description: u
                        }))}
                      label={LabelsPartsInfo.unitOfMeasure}
                      fullWidth
                    />
                  </Stack>
                  <ControlledMuiCheckboxSingle
                    name="taxExempt"
                    control={control}
                    label={LabelsPartsInfo.taxExempt}
                  />
                </Stack>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => cancelCallback()}>Cancel</Button>
        <Button
          variant="contained"
          type="submit"
          form="cannedJobMotorLaborForm">
          Save
        </Button>
      </DialogActions>
    </>
  )
}
